import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _isEmpty from "lodash/isEmpty";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es7.object.values.js";
import React from 'react';
import Tabs from '@nerdwallet/react-tabs';
import classes from '@nerdwallet/base-styles/classes';
import Accordion from '@nerdwallet/react-accordion';
import ExpandableContent from '../expandable-content';
import OptionalListContent from '../optional-list-content';
import TabContentImpression from './tab-content';
import TabImpression from './tab-impression';
import { ProductCardContentTabEnum } from '../types/graphql/generated-types';
import { sanitizeNode } from '../lib/utils';
import { TRK_PRODUCT_LOCATION } from '../lib/constants';

/**
 * Get label for tab.
 */
var getLabelForTab = function getLabelForTab(tab) {
  var _ProductCardContentTa;

  return (_ProductCardContentTa = {}, _defineProperty(_ProductCardContentTa, ProductCardContentTabEnum.WhyWeLikeIt, 'Why We Like It'), _defineProperty(_ProductCardContentTa, ProductCardContentTabEnum.ProsCons, 'Pros & Cons'), _defineProperty(_ProductCardContentTa, ProductCardContentTabEnum.ProductDetails, 'Product Details'), _defineProperty(_ProductCardContentTa, ProductCardContentTabEnum.KeyFacts, 'Key Facts'), _ProductCardContentTa)[tab];
};
/**
 * Generate tab config(s*) for a given tab if there's associated content.
 */


var generateTabConfigsForTab = function generateTabConfigsForTab(tab, content) {
  if (!content) {
    return [];
  }

  return [{
    id: tab,
    content: content,
    label: getLabelForTab(tab)
  }];
};

var noop = function noop() {};
/**
 * Display tab content with expanded content.
 */


var TabbedContent = function TabbedContent(_ref) {
  var analytics = _ref.analytics,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      id = _ref.id,
      isInitTabContentExpanded = _ref.isInitTabContentExpanded,
      _ref$reviewLinkNode = _ref.reviewLinkNode,
      reviewLinkNode = _ref$reviewLinkNode === void 0 ? null : _ref$reviewLinkNode,
      reviewLinkTabIndex = _ref.reviewLinkTabIndex,
      shouldOmitExpandFunctionalityOnMobile = _ref.shouldOmitExpandFunctionalityOnMobile,
      tabConfigs = _ref.tabConfigs,
      trkSectionPosition = _ref.trkSectionPosition;
  return /*#__PURE__*/React.createElement(Tabs, {
    id: "".concat(TRK_PRODUCT_LOCATION, "__tabs--").concat(id),
    className: className
  }, tabConfigs.map(function (_ref2, i) {
    var content = _ref2.content,
        label = _ref2.label,
        tabId = _ref2.id;
    var isFirstTab = i === 0;
    var tabContentImpression = /*#__PURE__*/React.createElement(TabContentImpression, {
      analytics: analytics === null || analytics === void 0 ? void 0 : analytics.tabContentProps,
      reviewLinkNode: reviewLinkTabIndex === i ? reviewLinkNode : null,
      tabId: id
    }, sanitizeNode(content));
    return /*#__PURE__*/React.createElement(TabImpression, {
      analytics: analytics === null || analytics === void 0 ? void 0 : analytics.tabProps,
      key: tabId,
      label: label
      /**
       * Required to be a function top-level but actual logic is called below.
       * @see {@link https://github.com/NerdWallet/react-components/blob/b2a5faaffdab5a75378b9dbe53f9bb0973caad58/components/tabs/src/controlled-tabs.jsx#L125}
       * @see {./tab-impression.TabImpression}
       */
      ,
      onTabClick: noop
    }, isFirstTab ? /*#__PURE__*/React.createElement(ExpandableContent, {
      analytics: (analytics === null || analytics === void 0 ? void 0 : analytics.expandableContentProps) || analytics,
      id: tabId,
      omitExpandFunctionalityOnMobile: shouldOmitExpandFunctionalityOnMobile,
      contentExpandedOnInitialization: isInitTabContentExpanded,
      trkSectionCategory: "".concat(TRK_PRODUCT_LOCATION, "__tabs--").concat(id),
      trkSectionPosition: trkSectionPosition
    }, tabContentImpression) : tabContentImpression);
  }));
};

/**
 * Base component for rendering content.
 */
var BaseContent = function BaseContent(_ref3) {
  var analytics = _ref3.analytics,
      bottomLine = _ref3.bottomLine,
      _ref3$className = _ref3.className,
      className = _ref3$className === void 0 ? '' : _ref3$className,
      cons = _ref3.cons,
      id = _ref3.id,
      _ref3$isInitTabConten = _ref3.isInitTabContentExpanded,
      isInitTabContentExpanded = _ref3$isInitTabConten === void 0 ? false : _ref3$isInitTabConten,
      keyFacts = _ref3.keyFacts,
      productDetails = _ref3.productDetails,
      pros = _ref3.pros,
      _ref3$reviewLinkNode = _ref3.reviewLinkNode,
      reviewLinkNode = _ref3$reviewLinkNode === void 0 ? null : _ref3$reviewLinkNode,
      _ref3$reviewLinkTabIn = _ref3.reviewLinkTabIndex,
      rawReviewLinkTabIndex = _ref3$reviewLinkTabIn === void 0 ? 1 : _ref3$reviewLinkTabIn,
      _ref3$shouldOmitExpan = _ref3.shouldOmitExpandFunctionalityOnMobile,
      shouldOmitExpandFunctionalityOnMobile = _ref3$shouldOmitExpan === void 0 ? false : _ref3$shouldOmitExpan,
      tabs = _ref3.tabs,
      trkSectionPosition = _ref3.trkSectionPosition,
      _ref3$hasAccordionDet = _ref3.hasAccordionDetails,
      hasAccordionDetails = _ref3$hasAccordionDet === void 0 ? false : _ref3$hasAccordionDet;
  var hasPros = !_isEmpty(pros);
  var hasCons = !_isEmpty(cons);
  var prosContent = /*#__PURE__*/React.createElement(OptionalListContent, {
    content: pros,
    contentLabel: "Pros",
    className: classes(['flex-1', {
      'padding-right-3--wide': hasCons
    }]),
    useLiPadding: true
  });
  var consContent = /*#__PURE__*/React.createElement(OptionalListContent, {
    content: cons,
    contentLabel: "Cons",
    className: classes(['flex-1', 'margin-top-0--wide', {
      'padding-left-3--wide': hasPros,
      'margin-top-3': hasPros
    }]),
    useLiPadding: true
  });
  var prosCons = /*#__PURE__*/React.createElement("div", {
    className: classes('display-flex--wide')
  }, prosContent, consContent);
  /**
   * Generate tab configs in order.
   */

  var tabConfigs = tabs === null || tabs === void 0 ? void 0 : tabs.reduce(function (configs, tab) {
    var currentConfigs;

    switch (tab) {
      case ProductCardContentTabEnum.KeyFacts:
        currentConfigs = generateTabConfigsForTab(tab, keyFacts);
        break;

      case ProductCardContentTabEnum.ProductDetails:
        currentConfigs = generateTabConfigsForTab(tab, productDetails);
        break;

      case ProductCardContentTabEnum.ProsCons:
        currentConfigs = generateTabConfigsForTab(tab, (hasPros || hasCons) && prosCons);
        break;

      case ProductCardContentTabEnum.WhyWeLikeIt:
        currentConfigs = generateTabConfigsForTab(tab, bottomLine);
        break;

      default:
    }

    return [].concat(_toConsumableArray(configs), _toConsumableArray(currentConfigs));
  }, []);

  if (_isEmpty(tabConfigs)) {
    return null;
  } // If filtered tabs is shorter than reviewLinkTabIndex put review link in the last tab


  var reviewLinkTabIndex = Math.min(tabConfigs.length - 1, rawReviewLinkTabIndex);

  var renderTabbedContent = function renderTabbedContent(isContentExpanded) {
    return /*#__PURE__*/React.createElement(TabbedContent, {
      id: id,
      analytics: analytics,
      className: className,
      isInitTabContentExpanded: isContentExpanded,
      reviewLinkNode: rawReviewLinkTabIndex >= 0 ? reviewLinkNode : null,
      reviewLinkTabIndex: reviewLinkTabIndex,
      shouldOmitExpandFunctionalityOnMobile: shouldOmitExpandFunctionalityOnMobile,
      tabConfigs: tabConfigs,
      trkSectionPosition: trkSectionPosition
    });
  };

  return hasAccordionDetails ? /*#__PURE__*/React.createElement(Accordion, {
    items: [{
      title: 'Show more',
      children: renderTabbedContent(true)
    }]
  }) : renderTabbedContent(isInitTabContentExpanded);
};

if (process.env.NODE_ENV !== 'production') {
  /* eslint-disable global-require, @typescript-eslint/no-var-requires, react/forbid-prop-types */
  var PropTypes = require('prop-types');

  BaseContent.propTypes = {
    analytics: PropTypes.object,
    bottomLine: PropTypes.node,
    className: PropTypes.string,
    cons: PropTypes.arrayOf(PropTypes.node),
    id: PropTypes.string,
    isInitTabContentExpanded: PropTypes.bool,
    keyFacts: PropTypes.node,
    productDetails: PropTypes.node,
    pros: PropTypes.arrayOf(PropTypes.node),
    reviewLinkNode: PropTypes.node,
    reviewLinkTabIndex: PropTypes.number,
    shouldOmitExpandFunctionalityOnMobile: PropTypes.bool,
    tabs: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ProductCardContentTabEnum))),
    trkSectionPosition: PropTypes.number,
    hasAccordionDetails: PropTypes.bool
  };
  TabbedContent.propTypes = {
    analytics: PropTypes.object,
    className: PropTypes.string,
    id: PropTypes.string,
    isInitTabContentExpanded: PropTypes.bool,
    reviewLinkNode: PropTypes.node,
    reviewLinkTabIndex: PropTypes.number,
    shouldOmitExpandFunctionalityOnMobile: PropTypes.bool,
    tabConfigs: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      id: PropTypes.string,
      content: PropTypes.node
    })),
    trkSectionPosition: PropTypes.bool
  };
  /* eslint-enable global-require, @typescript-eslint/no-var-requires, react/forbid-prop-types */
}

export default BaseContent;